import { config } from "./config";

// Utility function for formatting errors
export const formatError = (error) => {
  if (error instanceof Error) {
    return {
      message: error.message,
      // stack: config?.environment === 'development' ? error.stack : undefined
    };
  } else if (typeof error === 'object') {
    return error;
  } else {
    return { message: String(error) };
  }
};

// Define the function that logs only in the development environment
export const devLog = (type, ...args) => {
  // Check if the environment is development
  const isDevelopment = config?.environment === 'development';

  // If in development, log the arguments to the console
  if (isDevelopment) {
    if (type == 'error') {
      console.error(...args);
    }
    else {
      console.log(...args);
    }
  }
}

export const maskString = (input, visibleCount, maskChar = '*') => {
  // Handle edge cases

  if (!input) return;

  if (typeof input !== 'string') {
    throw new Error('Input must be a string');
  }

  if (typeof visibleCount !== 'number' || visibleCount < 0) {
    throw new Error('Visible count must be a non-negative number');
  }

  // If visibleCount is greater than or equal to the input length, return the input string
  if (visibleCount >= input.length) {
    return input;
  }

  // Calculate the masked part length
  const maskedLength = input.length - visibleCount;

  // Create masked part of the string and concatenate with the visible part
  const maskedPart = maskChar.repeat(maskedLength);
  const visiblePart = input.slice(-visibleCount);

  return maskedPart + visiblePart;
}

export const isSequential = (pin) => {
  const sequence = "0123456789";
  const reversedSequence = "9876543210";
  return sequence.includes(pin) || reversedSequence.includes(pin);
};

export const isRepeating = (pin) => {
  return /^(\d)\1+$/.test(pin);
};

export const getSdkConfigurationObject = (configToken) => {
  return {
    apiEnvironment: config?.environment,
    configToken,
  };
};

export const reactTranslation = (message) => {
  return Drupal.t(message);
};

export const getCurrencySymbol = (currencyCode) => {
  const mapping = {
    "AUD":	"A$",
    "BDT":	"৳",
    "CNY":	"¥",
    "HKD":	"HK$",
    "USD":	"$",
    "INR":	"₹",
    "IDR":	"Rp",
    "JPY":	"¥",
    "KRW":	"₩",
    "MYR":	"RM",
    "NPR":	"₨",
    "NZD":	"NZ$",
    "PKR":	"₨",
    "PHP":	"₱",
    "SGD":	"S$",
    "LKR":	"Rs",
    "THB":	"฿",
    "TRY":	"₺",
    "VND":	"₫",
    "BHD":	".د.ب",
    "ILS":	"₪",
    "AED":	"د.إ",
    "EUR":	"€",
    "BGN":	"лв",
    "CZK":	"Kč",
    "DKK":	"kr",
    "HUF":	"Ft",
    "NOK":	"kr",
    "PLN":	"zł",
    "RON":	"lei",
    "SEK":	"kr",
    "CHF":	"CHF",
    "GBP":	"£",
    "ARS":	"$",
    "BOB":	"Bs.",
    "BRL":	"R$",
    "CAD":	"C$",
    "CLP":	"$",
    "COP":	"$",
    "MXN":	"Mex$",
    "PEN":	"S/.",
    "PYG":	"₲",
    "UYU":	"$U",
    "AOA":	"Kz",
    "XOF":	"CFA",
    "BWP":	"P",
    "XAF":	"CFA",
    "EGP":	"E£",
    "GMD":	"D",
    "KES":	"KSh",
    "LSL":	"L",
    "MGA":	"Ar",
    "MWK":	"MK",
    "MAD":	"MAD",
    "NAD":	"N$",
    "NGN":	"₦",
    "RWF":	"FRw",
    "ZMW":	"ZK",
    "RSD":	"din",
  }

  return mapping[currencyCode] || currencyCode;
}

export const VALID_COUNTRY_CODES = new Set([
  "1",   // US/CA and NANP countries
  "20", "27",
  "30", "31", "32", "33", "34", "36", "39",
  "40", "41", "43", "44", "45", "46", "47", "48", "49",
  "51", "52", "53", "54", "55", "56", "57", "58",
  "60", "61", "62", "63", "64", "65", "66",
  "81", "82", "84", "86",
  "90", "91", "92", "93", "94", "95", "98",
  "211", "212", "213", "216",
  "220", "221", "222", "223", "224", "225", "226", "227",
  "228", "229",
  "230", "231", "232", "233", "234", "235", "236", "237",
  "238", "239",
  "240", "241", "242", "243", "244", "245", "246", "248",
  "249",
  "250", "251", "252", "253", "254", "255", "256", "257",
  "258", "260", "261", "262", "263", "264", "265", "266",
  "267", "268", "269",
  "290", "291", "297", "298", "299",
  "350", "351", "352", "353", "354", "355", "356", "357",
  "358", "359",
  "370", "371", "372", "373", "374", "375", "376", "377",
  "378", "380", "381", "382", "385", "386", "387", "389",
  "420", "421", "423",
  "500", "501", "502", "503", "504", "505", "506", "507",
  "508", "509",
  "590", "591", "592", "593", "594", "595", "596", "597",
  "598", "599",
  "670", "672", "673", "674", "675", "676", "677", "678",
  "679",
  "680", "681", "682", "683", "685", "686", "687", "688",
  "689",
  "690", "691", "692",
  "850", "852", "853", "855", "856",
  "870", "878", "880", "881", "882", "883", "886",
  "960", "961", "962", "963", "964", "965", "966", "967",
  "968", "970", "971", "972", "973", "974", "975", "976",
  "977",
  "992", "993", "994", "995", "996", "998"
]);

export const parsePhone = (phone) => {
  phone = phone.trim().replace(/\s+/g, "");

  // Basic E.164 check
  if (!/^\+[1-9]\d{7,14}$/.test(phone)) {
    return { valid: false, error: "Invalid phone number format" };
  }

  let digits = phone.slice(1); // remove leading "+"

  // Try splits from 10 digits down to 8 digits
  for (let localLength of [10, 9, 8]) {
    if (digits.length <= localLength) continue;

    const localNumber = digits.slice(-localLength);
    const countryCode = digits.slice(0, digits.length - localLength);

    if (VALID_COUNTRY_CODES.has(countryCode)) {
      return {
        valid: true,
        countryCode,
        number: localNumber
      };
    }
  }

  return { valid: false, error: "Unable to determine valid country code" };
};
